import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Bell, Send, RefreshCw, Clock } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';
import AdminLayout from '../../components/admin/AdminLayout';

const AdminBusReminders = () => {
  const [settings, setSettings] = useState(null);
  const [log, setLog] = useState([]);
  const [running, setRunning] = useState(false);

  const fetchAll = async () => {
    try {
      const [s, l] = await Promise.all([
        api.get('/admin/bus/reminders/settings'),
        api.get('/admin/bus/reminders/log?limit=20'),
      ]);
      setSettings(s.data);
      setLog(l.data.items || []);
    } catch (err) {
      setSettings({ enabled: false, hoursBefore: 12, sweepWindowMinutes: 30, template: null });
      setLog([]);
    }
  };

  const update = async (patch) => {
    try {
      const { data } = await api.put('/admin/bus/reminders/settings', patch);
      setSettings(data);
      toast({ title: 'Reminder settings updated' });
    } catch {
      toast({ title: 'Failed to update', variant: 'destructive' });
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post('/admin/bus/reminders/run-now');
      toast({
        title: 'Sweep run',
        description: `Evaluated ${data.evaluated}, sent ${data.sent}, skipped ${data.skipped || 0}`,
      });
      fetchAll();
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally {
      setRunning(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  return (
    <AdminLayout>
      <div className="max-w-3xl mx-auto p-4 md:p-6 space-y-6" data-testid="admin-bus-reminders-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Bell className="h-6 w-6 text-amber-500" /> Bus Departure Reminders
            </h1>
            <p className="text-sm text-gray-500">Auto-WhatsApp passengers T-N hours before their trip departs.</p>
          </div>
          <Button variant="outline" onClick={fetchAll} data-testid="reminders-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        </div>

        {!settings ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : (
          <Card className="p-5 space-y-4" data-testid="reminders-settings-card">
            <div className="flex items-center justify-between border-b pb-3">
              <div>
                <p className="font-semibold text-sm">Enabled</p>
                <p className="text-xs text-gray-500">Cron sweep runs every 15 minutes when on.</p>
              </div>
              <Switch
                checked={!!settings.enabled}
                onCheckedChange={(v) => update({ enabled: v })}
                data-testid="reminders-enabled-toggle"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium block mb-1">Hours before departure</label>
                <Input
                  type="number" min="1" max="48"
                  value={settings.hoursBefore}
                  onChange={(e) => setSettings({ ...settings, hoursBefore: parseInt(e.target.value, 10) || 12 })}
                  onBlur={() => update({ hoursBefore: settings.hoursBefore })}
                  data-testid="reminders-hours-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium block mb-1">Sweep window (min)</label>
                <Input
                  type="number" min="5" max="120"
                  value={settings.sweepWindowMinutes}
                  onChange={(e) => setSettings({ ...settings, sweepWindowMinutes: parseInt(e.target.value, 10) || 30 })}
                  onBlur={() => update({ sweepWindowMinutes: settings.sweepWindowMinutes })}
                  data-testid="reminders-window-input"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-medium block mb-1">Message template</label>
              <textarea
                rows={5}
                value={settings.template || ''}
                onChange={(e) => setSettings({ ...settings, template: e.target.value })}
                onBlur={() => update({ template: settings.template || '' })}
                placeholder="Use {ticket}, {origin}, {time}, {date}, {operator}. Leave blank for default."
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono"
                data-testid="reminders-template-input"
              />
              <p className="text-[11px] text-gray-400 mt-1">Placeholders: <code>{'{ticket}'}</code>, <code>{'{origin}'}</code>, <code>{'{time}'}</code>, <code>{'{date}'}</code>, <code>{'{operator}'}</code></p>
            </div>

            <Button
              onClick={runNow}
              disabled={running}
              className="w-full bg-amber-600 hover:bg-amber-700"
              data-testid="reminders-run-now-btn"
            >
              <Send className="h-4 w-4 mr-1" /> {running ? 'Sweeping…' : 'Run sweep now (force)'}
            </Button>
          </Card>
        )}

        <Card className="p-5" data-testid="reminders-log-card">
          <h2 className="font-semibold text-sm mb-3 flex items-center gap-2">
            <Clock className="h-4 w-4" /> Recent reminders
          </h2>
          {log.length === 0 ? (
            <p className="text-sm text-gray-500">No reminders sent yet.</p>
          ) : (
            <div className="space-y-1 text-xs" data-testid="reminders-log">
              {log.map((l) => (
                <div key={l.id} className="flex items-center justify-between py-1.5 border-b last:border-b-0">
                  <span className="font-mono">{l.ticketCode}</span>
                  <span className="text-gray-500">{l.passengerCount} pax</span>
                  <span className="text-gray-500">{new Date(l.sentAt).toLocaleString()}</span>
                  <span className={l.success ? 'text-emerald-600' : 'text-red-600'}>{l.success ? '✓' : '✗'}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminBusReminders;
